import React, { useState, useEffect } from 'react';
import { Calendar, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const statusColors = {
  'planning': '#fbbf24',
  'in-progress': '#3b82f6',
  'on-hold': '#f97316',
  'completed': '#10b981',
  'cancelled': '#ef4444'
};

const statusLabels = {
  'planning': 'Planning',
  'in-progress': 'In Progress',
  'on-hold': 'On Hold',
  'completed': 'Completed',
  'cancelled': 'Cancelled'
};

export default function ProjectsTab({ portalUser }) {
  const [projects, setProjects] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectSort, setProjectSort] = useState('newest');
  const [projectPage, setProjectPage] = useState(1);
  const projectPageSize = 4;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewProject, setReviewProject] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, reviewText: '', displayName: '', consentToPublish: false });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [submittedByProject, setSubmittedByProject] = useState({});
  const [queryReviewHandled, setQueryReviewHandled] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchMyReviews();
  }, [portalUser]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('clientToken') || localStorage.getItem('token');
      const clientId = localStorage.getItem('clientId') || portalUser?.id || portalUser?._id;

      if (!token || !clientId) {
        setError('Not authenticated');
        setProjects([]);
        return;
      }

      const res = await axios.get(`${API_URL}/clients/${clientId}/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setProjects(res.data);
      setError('');
    } catch (err) {
      setError('Failed to load projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (progress) => {
    if (progress < 33) return '#ef4444';
    if (progress < 66) return '#f97316';
    return '#10b981';
  };

  const fetchMyReviews = async () => {
    try {
      const token = localStorage.getItem('clientToken') || localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API_URL}/reviews/mine`, { headers: { 'Authorization': `Bearer ${token}` } });
      const rows = Array.isArray(res.data) ? res.data : [];
      const map = rows.reduce((acc, r) => {
        if (r?.project) acc[String(r.project)] = r;
        return acc;
      }, {});
      setSubmittedByProject(map);
    } catch (err) {
      // Non-blocking: review map is optional UI enhancement
    }
  };

  const openReviewModal = (project) => {
    setReviewProject(project);
    setReviewError('');
    setReviewForm({
      rating: 5,
      reviewText: '',
      displayName: portalUser?.name || '',
      consentToPublish: false,
    });
  };

  const submitReview = async () => {
    if (!reviewProject?._id) return;
    if (!String(reviewForm.reviewText || '').trim()) {
      setReviewError('Please enter your review.');
      return;
    }
    try {
      setReviewSubmitting(true);
      setReviewError('');
      const token = localStorage.getItem('clientToken') || localStorage.getItem('token');
      await axios.post(`${API_URL}/reviews`, {
        projectId: reviewProject._id,
        rating: Number(reviewForm.rating || 5),
        reviewText: String(reviewForm.reviewText || '').trim(),
        displayName: String(reviewForm.displayName || '').trim(),
        consentToPublish: Boolean(reviewForm.consentToPublish),
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      await fetchMyReviews();
      setReviewProject(null);
    } catch (err) {
      setReviewError(err.response?.data?.error || 'Failed to submit review');
    } finally {
      setReviewSubmitting(false);
    }
  };

  useEffect(() => {
    if (queryReviewHandled) return;
    if (!projects.length) return;

    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('reviewProject');
    if (!projectId) return;

    const target = projects.find((p) => String(p._id) === String(projectId));
    if (!target || target.status !== 'completed' || submittedByProject[String(projectId)]) {
      setQueryReviewHandled(true);
      return;
    }

    openReviewModal(target);
    setQueryReviewHandled(true);
    params.delete('reviewProject');
    const query = params.toString();
    const nextUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    try { window.history.replaceState({}, '', nextUrl); } catch (e) { /* ignore */ }
  }, [projects, submittedByProject, queryReviewHandled]);

  const sortedProjects = [...projects].sort((a, b) => {
    if (projectSort === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    if (projectSort === 'progress_high') return Number(b.progress || 0) - Number(a.progress || 0);
    if (projectSort === 'progress_low') return Number(a.progress || 0) - Number(b.progress || 0);
    if (projectSort === 'due_soon') return new Date(a.dueDate || 0) - new Date(b.dueDate || 0);
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
  const projectTotalPages = Math.max(1, Math.ceil(sortedProjects.length / projectPageSize));
  const pagedProjects = sortedProjects.slice((projectPage - 1) * projectPageSize, projectPage * projectPageSize);

  useEffect(() => {
    setProjectPage(1);
  }, [projectSort, projects.length]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading projects...</div>;
  }

  return (
    <div>
      {error && <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '0.5rem', color: '#f87171', fontSize: '0.875rem' }}>{error}</div>}
      
      {projects.length === 0 ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>No active projects</div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Showing {pagedProjects.length} of {projects.length} projects</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Sort</span>
              <select value={projectSort} onChange={(e) => setProjectSort(e.target.value)} style={{ padding: '0.45rem 0.6rem', borderRadius: '0.5rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="due_soon">Due Soon</option>
                <option value="progress_high">Progress High-Low</option>
                <option value="progress_low">Progress Low-High</option>
              </select>
            </div>
          </div>
          {pagedProjects.map(project => {
            const isExpanded = !!expandedProjects[project._id];
            const alreadyReviewed = Boolean(submittedByProject[String(project._id)]);
            return (
            <div key={project._id} style={{ background: 'rgba(30,41,59,0.3)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '1rem', padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: 600 }}>{project.name}</h3>
                  <p style={{ margin: 0, fontSize: '0.875rem', color: '#94a3b8' }}>{project.description || 'No description yet.'}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <button onClick={() => setExpandedProjects((prev) => ({ ...prev, [project._id]: !prev[project._id] }))} style={{ padding: '0.45rem 0.75rem', borderRadius: '0.45rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1', background: 'rgba(15,23,42,0.35)', fontSize: '0.8rem' }}>
                    {isExpanded ? 'Hide' : 'View'}
                  </button>
                  <div style={{ padding: '0.5rem 1rem', background: statusColors[project.status], color: '#020617', borderRadius: '0.375rem', fontSize: '0.875rem', fontWeight: '500', minWidth: 'fit-content' }}>
                    {statusLabels[project.status]}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  <span style={{ color: '#94a3b8' }}>Progress</span>
                  <span style={{ color: getProgressColor(project.progress), fontWeight: '600' }}>{project.progress}%</span>
                </div>
                <div style={{ width: '100%', height: '0.5rem', background: 'rgba(15,23,42,0.5)', borderRadius: '9999px', overflow: 'hidden' }}>
                  <div style={{ width: `${project.progress}%`, height: '100%', background: getProgressColor(project.progress), transition: 'width 0.3s ease' }} />
                </div>
              </div>

              {isExpanded && (
              <>
              {/* Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                {project.startDate && (
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'start' }}>
                    <Calendar size={18} style={{ color: '#64748b', flexShrink: 0, marginTop: '0.125rem' }} />
                    <div style={{ fontSize: '0.875rem' }}>
                      <p style={{ margin: '0 0 0.25rem 0', color: '#94a3b8' }}>Started</p>
                      <p style={{ margin: 0 }}>{new Date(project.startDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
                {project.dueDate && (
                  <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'start' }}>
                    <Clock size={18} style={{ color: '#64748b', flexShrink: 0, marginTop: '0.125rem' }} />
                    <div style={{ fontSize: '0.875rem' }}>
                      <p style={{ margin: '0 0 0.25rem 0', color: '#94a3b8' }}>Due</p>
                      <p style={{ margin: 0 }}>{new Date(project.dueDate).toLocaleDateString()}</p>
                    </div>
                  </div>
                )}
              </div>

              {project.notes && (
                <div style={{ padding: '0.75rem', background: 'rgba(15,23,42,0.3)', borderRadius: '0.375rem', fontSize: '0.875rem', color: '#cbd5e1', marginBottom: '1rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#94a3b8', fontSize: '0.75rem', fontWeight: '500' }}>Notes</p>
                  {project.notes}
                </div>
              )}

              {project.status === 'completed' && project.completedDate && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontSize: '0.875rem', color: '#10b981' }}>
                  <CheckCircle size={16} />
                  Completed on {new Date(project.completedDate).toLocaleDateString()}
                </div>
              )}

              {project.status === 'completed' && (
                <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    {project.reviewRequestedAt ? `Review requested ${new Date(project.reviewRequestedAt).toLocaleDateString()}` : 'You can leave a review for this completed project.'}
                  </div>
                  <button
                    type="button"
                    onClick={() => openReviewModal(project)}
                    disabled={alreadyReviewed}
                    style={{
                      padding: '0.45rem 0.75rem',
                      borderRadius: '0.45rem',
                      border: '1px solid rgba(52,211,153,0.35)',
                      color: '#34d399',
                      fontSize: '0.8rem',
                      opacity: alreadyReviewed ? 0.6 : 1
                    }}
                  >
                    {alreadyReviewed ? 'Review submitted' : 'Leave Review'}
                  </button>
                </div>
              )}
              </>
              )}
            </div>
          )})}
          {projects.length > projectPageSize && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.25rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Page {projectPage} of {projectTotalPages}</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={() => setProjectPage((p) => Math.max(1, p - 1))} disabled={projectPage <= 1} style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', opacity: projectPage <= 1 ? 0.5 : 1 }}>Prev</button>
                <button onClick={() => setProjectPage((p) => Math.min(projectTotalPages, p + 1))} disabled={projectPage >= projectTotalPages} style={{ padding: '0.4rem 0.75rem', borderRadius: '0.5rem', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', opacity: projectPage >= projectTotalPages ? 0.5 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {reviewProject && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70, padding: '1rem' }}>
          <div style={{ width: '100%', maxWidth: '34rem', background: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '1rem', padding: '1rem' }}>
            <h3 style={{ margin: '0 0 0.35rem 0' }}>Leave Review</h3>
            <p style={{ margin: '0 0 0.8rem 0', fontSize: '0.85rem', color: '#94a3b8' }}>{reviewProject.name}</p>

            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#94a3b8', fontSize: '0.8rem' }}>Rating</label>
                <select value={reviewForm.rating} onChange={(e) => setReviewForm((prev) => ({ ...prev, rating: Number(e.target.value) }))} style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: '0.65rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                  <option value={5}>5 - Excellent</option>
                  <option value={4}>4 - Very good</option>
                  <option value={3}>3 - Good</option>
                  <option value={2}>2 - Fair</option>
                  <option value={1}>1 - Poor</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#94a3b8', fontSize: '0.8rem' }}>Display name (optional)</label>
                <input value={reviewForm.displayName} onChange={(e) => setReviewForm((prev) => ({ ...prev, displayName: e.target.value }))} placeholder="How your name should appear" style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: '0.65rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.3rem', color: '#94a3b8', fontSize: '0.8rem' }}>Review</label>
                <textarea value={reviewForm.reviewText} onChange={(e) => setReviewForm((prev) => ({ ...prev, reviewText: e.target.value }))} placeholder="Share your experience..." rows={5} style={{ width: '100%', padding: '0.65rem 0.75rem', borderRadius: '0.65rem', background: 'rgba(15,23,42,0.5)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', resize: 'vertical' }} />
              </div>
              <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'start', color: '#cbd5e1', fontSize: '0.85rem' }}>
                <input type="checkbox" checked={Boolean(reviewForm.consentToPublish)} onChange={(e) => setReviewForm((prev) => ({ ...prev, consentToPublish: e.target.checked }))} />
                I allow PageHyve to publish this review publicly (name may be anonymized to initials).
              </label>
            </div>

            {reviewError ? <div style={{ marginTop: '0.75rem', color: '#f87171', fontSize: '0.85rem' }}>{reviewError}</div> : null}

            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.65rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setReviewProject(null)} style={{ padding: '0.55rem 0.85rem', borderRadius: '0.55rem', border: '1px solid rgba(255,255,255,0.12)', color: '#cbd5e1' }}>Cancel</button>
              <button type="button" onClick={submitReview} disabled={reviewSubmitting} style={{ padding: '0.55rem 0.85rem', borderRadius: '0.55rem', background: 'linear-gradient(to right, #10b981, #06b6d4)', color: '#02121a', fontWeight: 600, opacity: reviewSubmitting ? 0.7 : 1 }}>
                {reviewSubmitting ? 'Submitting...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
